import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import { FishImage } from '../components/GameImages';
import '../css.css';

// 수역 목록 정의
const MAP_OPTIONS = [
    { value: '', label: '전체 수역' },
    { value: 'mosquito_lake_l', label: '모기 호수' },
    { value: 'american_pond_l', label: '엘크 호수' },
    { value: 'winding_rivulet_l', label: '굽이치는 개울' },
    { value: 'old_burg_lake_l', label: '올드버그 호수' },
    { value: 'belaya_river_l', label: '벨라야 강' },
    { value: 'kuori_lake_l', label: '쿠오리 호수' },
    { value: 'bear_lake_l', label: '곰 호수' },
    { value: 'volkhov_river_l', label: '볼호프 강' },
    { value: 'seversky_donets_river_l', label: '세베르스키 도네츠 강' },
    { value: 'sura_river_l', label: '수라 강' },
    { value: 'ladoga_lake_l', label: '라도가 호수' },
    { value: 'the_amber_lake_l', label: '엠버 호수' },
    { value: 'ladoga_archipelago_l', label: '라도가 군도' },
    { value: 'akhtuba_river_l', label: '악투바 강' },
    { value: 'copper_lake_l', label: '카퍼 호수' },
    { value: 'lower_tunguska_river_l', label: '니즈냐야 퉁구스카 강' },
    { value: 'yama_river_l', label: '야마 강' },
    { value: 'norwegian_sea_l', label: '노르웨이 해' }
];

// 서식 수역 목록을 추출하는 함수
const getHabitatMaps = (trophy) => {
    return MAP_OPTIONS
        .filter(opt => opt.value && trophy[opt.value] === 1)
        .map(opt => opt.label);
};

// 상세보기 모달 컴포넌트
const TrophyDetailModal = memo(({ trophy, onClose }) => {
    const [isHabitatOpen, setIsHabitatOpen] = useState(true);

    if (!trophy) return null;

    const habitatMaps = getHabitatMaps(trophy);

    return (
        <div className="trophy-detail-overlay" onClick={onClose}>
            <div className="trophy-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div className="trophy-detail-header">
                    <div className="trophy-detail-fish-info">
                        <FishImage
                            filename={trophy.filename}
                            alt={trophy.name_ko}
                            className="trophy-detail-fish-image"
                        />
                        <div className="trophy-detail-name-area">
                            <span className="trophy-detail-name">{trophy.name_ko}</span>
                            {trophy.rarity && trophy.rarity !== 'common' && (
                                <img 
                                    src={`${process.env.PUBLIC_URL}/data/img/${trophy.rarity}.webp`}
                                    alt={trophy.rarity}
                                    className="trophy-detail-rarity-img"
                                />
                            )}
                        </div>
                    </div>
                    <button className="trophy-detail-close" onClick={onClose}>✕</button>
                </div>

                {/* 크기 정보 */}
                <div className="trophy-detail-body">
                    <div className="trophy-detail-sizes">
                        <div className="trophy-detail-size-row">
                            <span className="trophy-detail-size-label">최소 크기</span>
                            <span className="trophy-detail-size-value">{formatWeight(trophy.min_size) || '-'}</span>
                        </div>
                        <div className="trophy-detail-size-row">
                            <span className="trophy-detail-size-label">마크급 크기</span>
                            <span className="trophy-detail-size-value">{formatWeight(trophy.mark_size) || '-'}</span>
                        </div>
                        <div className="trophy-detail-size-row">
                            <span className="trophy-detail-size-label">로그급 크기</span>
                            <span className="trophy-detail-size-value">{formatWeight(trophy.record_size) || '-'}</span>
                        </div>
                        <div className="trophy-detail-size-row">
                            <span className="trophy-detail-size-label">트로피</span>
                            <span className="trophy-detail-size-value trophy-highlight">{formatWeight(trophy.trophy) || '-'}</span>
                        </div>
                        <div className="trophy-detail-size-row">
                            <span className="trophy-detail-size-label">레어 트로피</span>
                            <span className="trophy-detail-size-value trophy-rare-highlight">{formatWeight(trophy.rare_trophy) || '-'}</span>
                        </div>
                    </div>

                    {/* 서식 수역 */}
                    <div className="trophy-detail-habitat">
                        <button 
                            className="trophy-habitat-toggle"
                            onClick={() => setIsHabitatOpen(!isHabitatOpen)}
                        >
                            <span>🗺️ 서식 수역 ({habitatMaps.length})</span>
                            <span className={`trophy-habitat-arrow ${isHabitatOpen ? 'open' : ''}`}>▼</span>
                        </button>
                        {isHabitatOpen && (
                            <div className="trophy-habitat-list">
                                {habitatMaps.length > 0 ? (
                                    habitatMaps.map((mapName, idx) => (
                                        <span key={idx} className="trophy-habitat-tag">
                                            📍 {mapName}
                                        </span>
                                    ))
                                ) : (
                                    <span className="trophy-habitat-empty">등록된 서식 수역이 없습니다.</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

// 무게를 적절한 단위로 포맷하는 함수
const formatWeight = (weightInGrams) => {
    if (!weightInGrams || weightInGrams === 'NULL' || weightInGrams === null) {
        return '';
    }
    
    // 문자열을 숫자로 변환
    const weight = parseInt(weightInGrams);
    
    if (isNaN(weight)) {
        return weightInGrams; // 숫자가 아닌 경우 원본 반환
    }
    
    if (weight >= 1000) {
        // 1000g 이상은 kg 단위로 표시
        const kgValue = weight / 1000;
        if (kgValue % 1 === 0) {
            return `${kgValue} kg`;
        } else {
            const formatted = kgValue.toFixed(2);
            return `${parseFloat(formatted)} kg`;
        }
    } else {
        // 1000g 미만은 g 단위로 표시
        return `${weight} g`;
    }
};

// 레어도 이미지 컴포넌트 - 메모이제이션
const RarityImage = memo(({ rarity }) => {
    if (rarity === 'rare') {
        return (
            <img 
                src={`${process.env.PUBLIC_URL}/data/img/rare.webp`}
                alt="rare"
                className="raregrade"
                style={{ height: '44px' }}
            />
        );
    } else if (rarity === 'veryrare') {
        return (
            <img 
                src={`${process.env.PUBLIC_URL}/data/img/veryrare.webp`}
                alt="veryrare"
                className="raregrade"
                style={{ height: '44px' }}
            />
        );
    } else if (rarity === 'common') {
        return null; // 빈 칸
    }
    return <span>{rarity}</span>;
});

// 트로피 테이블 행 컴포넌트 - 메모이제이션
const TrophyRow = memo(({ trophy, index, onDetailClick }) => {
    return (
        <tr>
            <td style={{ width: '100px', textAlign: 'center' }}>
                <FishImage
                    filename={trophy.filename}
                    alt={trophy.name_ko}
                    className="fish-image"
                    onError={() => {
                        console.warn('Failed to load trophy fish image:', trophy.filename);
                    }}
                />
            </td>
            <td>{trophy.name_ko}</td>
            <td>{formatWeight(trophy.min_size)}</td>
            <td>{formatWeight(trophy.mark_size)}</td>
            <td>{formatWeight(trophy.record_size)}</td>
            <td>{formatWeight(trophy.trophy)}</td>
            <td>{formatWeight(trophy.rare_trophy)}</td>
            <td style={{ width: '100px' }}>
                <RarityImage rarity={trophy.rarity} />
            </td>
            <td style={{ width: '70px', textAlign: 'center' }}>
                <button 
                    className="trophy-detail-btn"
                    onClick={() => onDetailClick(trophy)}
                    title="상세보기"
                >
                    🔍
                </button>
            </td>
        </tr>
    );
}, (prevProps, nextProps) => {
    return prevProps.trophy === nextProps.trophy;
});

const Page1 = () => {
    const [trophyData, setTrophyData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMap, setSelectedMap] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isHeaderFixed, setIsHeaderFixed] = useState(false);
    const [detailTrophy, setDetailTrophy] = useState(null);
    
    const tableRef = useRef(null);
    const headerRef = useRef(null);
    const cloneHeaderRef = useRef(null);

    // 스크롤 이벤트 핸들러
    useEffect(() => {
        const handleScroll = () => {
            if (!tableRef.current || !headerRef.current) return;

            const tableRect = tableRef.current.getBoundingClientRect();
            const headerHeight = 65; // 페이지 헤더 높이

            // 테이블이 헤더 아래로 올라갔을 때 고정
            if (tableRect.top <= headerHeight && tableRect.bottom > headerHeight) {
                setIsHeaderFixed(true);
            } else {
                setIsHeaderFixed(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        handleScroll(); // 초기 상태 체크
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);

    const fetchTrophyData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('/api/trophy');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setTrophyData(data);
            setFilteredData(data);
        } catch (error) {
            console.error('Error fetching trophy data:', error);
            setError('트로피 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    // 검색 + 수역 필터링 함수
    const applyFilters = useCallback((term, mapFilter) => {
        let filtered = trophyData;

        // 수역 필터
        if (mapFilter) {
            filtered = filtered.filter(trophy => trophy[mapFilter] === 1);
        }

        // 검색어 필터
        if (term.trim()) {
            filtered = filtered.filter(trophy =>
                trophy.name_ko?.toLowerCase().includes(term.toLowerCase())
            );
        }

        setFilteredData(filtered);
    }, [trophyData]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        applyFilters(term, selectedMap);
    }, [selectedMap, applyFilters]);

    const handleMapFilter = useCallback((mapValue) => {
        setSelectedMap(mapValue);
        applyFilters(searchTerm, mapValue);
    }, [searchTerm, applyFilters]);

    useEffect(() => {
        fetchTrophyData();
    }, [fetchTrophyData]);

    // 테이블 렌더링 메모이제이션
    const renderTable = useMemo(() => {
        if (filteredData.length === 0 && searchTerm) {
            return (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    검색 결과가 없습니다.
                </div>
            );
        }
        
        if (filteredData.length === 0) return null;

        return (
            <>
                {/* 고정 헤더 (스크롤 시 표시) */}
                {isHeaderFixed && (
                    <div 
                        ref={cloneHeaderRef}
                        className="fixed-table-header"
                        style={{
                            position: 'fixed',
                            top: '65px',
                            left: '0',
                            right: '0',
                            zIndex: 99,
                            backgroundColor: 'var(--bg-primary, white)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            padding: '0 20px',
                            overflowX: 'auto'
                        }}
                    >
                        <table className="oil-table trophy-table-fixed" style={{ 
                            marginBottom: 0,
                            tableLayout: 'fixed',
                            minWidth: '870px'
                        }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '100px' }}>사진</th>
                                    <th>어종명</th>
                                    <th>최소크기</th>
                                    <th>마크급 크기</th>
                                    <th>로그급 크기</th>
                                    <th>트로피</th>
                                    <th>레어 트로피</th>
                                    <th style={{ width: '100px' }}>레어도</th>
                                    <th style={{ width: '70px' }}>상세</th>
                                </tr>
                            </thead>
                        </table>
                    </div>
                )}

                {/* 실제 테이블 */}
                <div className="table-wrapper">
                    <table ref={tableRef} className="oil-table trophy-table-fixed" style={{
                        tableLayout: 'fixed',
                        minWidth: '870px'
                    }}>
                        <thead ref={headerRef}>
                            <tr>
                                <th style={{ width: '100px' }}>사진</th>
                                <th>어종명</th>
                                <th>최소크기</th>
                                <th>마크급 크기</th>
                                <th>로그급 크기</th>
                                <th>트로피</th>
                                <th>레어 트로피</th>
                                <th style={{ width: '100px' }}>레어도</th>
                                <th style={{ width: '70px' }}>상세</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((trophy, index) => (
                                <TrophyRow 
                                    key={trophy.id || index} 
                                    trophy={trophy} 
                                    index={index}
                                    onDetailClick={setDetailTrophy}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    }, [filteredData, searchTerm, isHeaderFixed, setDetailTrophy]);

    if (loading) {
        return (
            <>
                <h1 className="page-title">트로피 사이즈</h1>
                <div className="spinner-container">
                    <LoadingSpinner
                        height={80}
                        width={80}
                        color="#4fa94d"
                        visible={true}
                    />
                    <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                        데이터 로딩 중...
                    </div>
                </div>
                <Comments pageName="trophy" />
            </>
        );
    }

    if (error) {
        return (
            <>
                <h1 className="page-title">트로피 사이즈</h1>
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchTrophyData}>다시 시도</button>
                </div>
                <Comments pageName="trophy" />
            </>
        );
    }

    return (
        <>
            <h1 className="page-title">트로피 사이즈</h1>
            <div className="page-content">
                {/* 검색 + 수역 필터 */}
                <div className="trophy-filter-container">
                    <div className="trophy-search-wrapper">
                        <input
                            type="text"
                            placeholder="어종명으로 검색"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="trophy-search-input"
                        />
                        {searchTerm && (
                            <button 
                                className="trophy-search-clear"
                                onClick={() => handleSearch('')}
                                title="검색어 초기화"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <div className="trophy-map-filter-wrapper">
                        <select
                            value={selectedMap}
                            onChange={(e) => handleMapFilter(e.target.value)}
                            className="trophy-map-select"
                        >
                            {MAP_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {(searchTerm || selectedMap) && (
                    <div className="trophy-filter-result-count">
                        {filteredData.length}개의 결과가 찾아졌습니다.
                        {selectedMap && (
                            <span className="trophy-active-filter">
                                📍 {MAP_OPTIONS.find(m => m.value === selectedMap)?.label}
                            </span>
                        )}
                    </div>
                )}
                
                {/* 테이블 렌더링 */}
                {renderTable}
            </div>

            {/* 상세보기 모달 */}
            {detailTrophy && (
                <TrophyDetailModal 
                    trophy={detailTrophy} 
                    onClose={() => setDetailTrophy(null)} 
                />
            )}

            <Comments pageName="trophy" />
        </>
    );
};

export default Page1;